/**
 * Calendar event fetching with polling.
 * Step 1: POST /api_ats/v1/calendar_events/trigger_fetch → returns { fetch_uuid }
 * Step 2: GET  /api_ats/v1/calendar_events?fetch_uuid=...  → poll until events ready
 *
 * Used to show interviewer busy/free times on the calendar.
 * Polling logic will be refined — keeping the hook interface stable.
 */
import { useCallback, useRef, useState } from 'react';
import { http } from '@/lib/ats-https';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';

export interface CalendarEventParams {
  userIds: number[];
  startDate: string;
  endDate: string;
  timezone: string;
}

export interface CalendarEvent {
  id?: string;
  title?: string;
  start: string;
  end: string;
  user_id?: number;
  status?: string;
  [key: string]: unknown;
}

interface CalendarEventsState {
  events: CalendarEvent[];
  isLoading: boolean;
  error: Error | null;
}

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

async function triggerFetch(params: CalendarEventParams): Promise<string> {
  const res = await http.post('/calendar_events/trigger_fetch', {
    user_ids: params.userIds,
    start_date: params.startDate,
    end_date: params.endDate,
    time_zone: params.timezone,
  });
  assertResolvedAtsNotHttpError(res);
  const data = (res as { data?: { fetch_uuid?: string } })?.data ?? res;
  return (data as { fetch_uuid?: string }).fetch_uuid ?? '';
}

async function pollEvents(fetchUuid: string): Promise<CalendarEvent[]> {
  const res = await http.get('/calendar_events', {
    params: { fetch_uuid: fetchUuid },
  });
  assertResolvedAtsNotHttpError(res);
  const data = (res as { data?: unknown })?.data ?? res;
  if (Array.isArray(data)) {
    return data as CalendarEvent[];
  }
  return [];
}

/**
 * Hook for fetching calendar events with trigger + poll pattern.
 * Call `fetchEvents(params)` to start; events update as polling completes.
 */
export function useCalendarEvents() {
  const [state, setState] = useState<CalendarEventsState>({
    events: [],
    isLoading: false,
    error: null,
  });
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchEvents = useCallback(
    async (params: CalendarEventParams) => {
      cleanup();
      setState({ events: [], isLoading: true, error: null });

      try {
        const fetchUuid = await triggerFetch(params);
        if (!fetchUuid) {
          setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        let attempts = 0;
        const poll = async () => {
          try {
            const events = await pollEvents(fetchUuid);
            if (events.length > 0 || attempts >= MAX_POLLS) {
              setState({ events, isLoading: false, error: null });
              return;
            }
            attempts++;
            pollRef.current = setTimeout(poll, POLL_INTERVAL);
          } catch (err) {
            setState({
              events: [],
              isLoading: false,
              error: err instanceof Error ? err : new Error(String(err)),
            });
          }
        };

        await poll();
      } catch (err) {
        setState({
          events: [],
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    },
    [cleanup]
  );

  return {
    ...state,
    fetchEvents,
    cleanup,
  };
}
